import type { InvoiceConsistencyData as InvoiceData } from './schemas';
type Fraction = { n: bigint; d: bigint };
const zero: Fraction = { n: 0n, d: 1n };
const add = (a: Fraction, b: Fraction): Fraction => a.d === b.d
  ? { n: a.n + b.n, d: a.d }
  : { n: a.n * b.d + b.n * a.d, d: a.d * b.d };
const multiply = (a: Fraction, b: Fraction): Fraction => ({ n: a.n * b.n, d: a.d * b.d });
const divide = (a: Fraction, b: Fraction): Fraction => ({ n: a.n * b.d, d: a.d * b.n });
const absolute = (n: bigint) => n < 0n ? -n : n;

// Decimal source values, including scientific notation, without binary float arithmetic.
function decimal(value: number): Fraction {
  const [coefficient, exponent = '0'] = String(value).toLowerCase().split('e');
  const [whole, fraction = ''] = coefficient.split('.');
  const scale = fraction.length - Number(exponent);
  const n = BigInt(whole + fraction);
  return scale >= 0 ? { n, d: 10n ** BigInt(scale) } : { n: n * 10n ** BigInt(-scale), d: 1n };
}
function cents(value: Fraction): bigint {
  const scaled = absolute(value.n) * 100n;
  const rounded = (scaled * 2n + value.d) / (2n * value.d);
  return value.n < 0n ? -rounded : rounded;
}
const money = (value: number) => cents(decimal(value));
const sumMoney = (values: number[]) => values.reduce((sum, value) => sum + money(value), 0n);
const amount = (value: bigint) => Number(value) / 100;

export interface InvoiceConsistencyIssue {
  code: string;
  path: (string | number)[];
  message: string;
}

/** Checks printed values; never recalculates or mutates the extracted invoice. */
export function validateInvoiceConsistency(data: InvoiceData): InvoiceConsistencyIssue[] {
  const issues: InvoiceConsistencyIssue[] = [];
  const issue = (code: string, path: (string | number)[], message: string) => issues.push({ code, path, message });
  // Zod may call a refinement after a finite/positive validation has already failed.
  const finite = (value: unknown): boolean => typeof value === 'number' ? Number.isFinite(value)
    : Array.isArray(value) ? value.every(finite)
    : value !== null && typeof value === 'object' ? Object.values(value).every(finite) : true;
  if (!finite(data)) return issues;
  const check = (actual: number | undefined, expected: bigint, path: (string | number)[], code: string) => {
    if (actual !== undefined && absolute(money(actual) - expected) > 1n) {
      issue(code, path, `${code}: Betrag ist inkonsistent; erwartet ${amount(expected).toFixed(2)}.`);
    }
  };
  const groups = new Map<string, { base: bigint; rate: number; category: string }>();
  const key = (category: string, rate: number) => `${category}:${rate}`;
  const addGroup = (category: string, rate: number, base: bigint) => {
    const id = key(category, rate);
    const current = groups.get(id);
    groups.set(id, { base: (current?.base ?? 0n) + base, rate, category });
  };
  const zeroTax = new Set(['Z', 'E', 'AE', 'K', 'G', 'O']);
  data.lineItems.forEach((line, index) => {
    const path = ['lineItems', index];
    if (line.quantity <= 0 || (line.unitPriceBaseQuantity ?? 1) <= 0) return;
    let net = divide(multiply(decimal(line.quantity), decimal(line.unitPrice)), decimal(line.unitPriceBaseQuantity ?? 1));
    for (const adjustment of line.allowancesCharges ?? []) {
      net = add(net, decimal(adjustment.amount * (adjustment.chargeIndicator ? 1 : -1)));
    }
    check(line.lineTotalAmount, cents(net), [...path, 'lineTotalAmount'], 'LINE_NET_MISMATCH');
    if (zeroTax.has(line.taxCategory) && line.taxRate !== 0) {
      issue('TAX_CATEGORY_RATE_MISMATCH', [...path, 'taxRate'], 'Steuerfreie Position benötigt einen Steuersatz von 0.');
    }
    const tax = divide(multiply(decimal(line.lineTotalAmount), decimal(line.taxRate)), decimal(100));
    check(line.taxAmount, cents(tax), [...path, 'taxAmount'], 'LINE_TAX_MISMATCH');
    check(line.unitTaxAmount, cents(divide(tax, decimal(line.quantity))), [...path, 'unitTaxAmount'], 'UNIT_TAX_MISMATCH');
    check(line.lineGrossAmount, money(line.lineTotalAmount) + cents(tax), [...path, 'lineGrossAmount'], 'LINE_GROSS_MISMATCH');
    addGroup(line.taxCategory, line.taxRate, money(line.lineTotalAmount));
  });

  if (data.totalQuantity !== undefined) {
    const units = new Set(data.lineItems.map(line => line.unitCode));
    const unit = data.totalQuantityUnitCode ?? (units.size === 1 ? data.lineItems[0]?.unitCode : undefined);
    if (!unit || !units.has(unit)) {
      issue('QUANTITY_UNIT_REQUIRED', ['totalQuantity'], 'Gesamtmenge benötigt eine zu den Positionen passende Einheit.');
    } else {
      const total = data.lineItems.filter(line => line.unitCode === unit).reduce((sum, line) => add(sum, decimal(line.quantity)), zero);
      const actual = decimal(data.totalQuantity);
      if (absolute(actual.n * total.d - total.n * actual.d) * 1000000n > actual.d * total.d) {
        issue('QUANTITY_TOTAL_MISMATCH', ['totalQuantity'], 'Gesamtmenge stimmt nicht mit der Positionsmenge derselben Einheit überein.');
      }
    }
  }
  const lines = sumMoney(data.lineItems.map(line => line.lineTotalAmount));
  const adjustments = data.allowancesCharges ?? [];
  const allowances = sumMoney(adjustments.filter(item => !item.chargeIndicator).map(item => item.amount));
  const charges = sumMoney(adjustments.filter(item => item.chargeIndicator).map(item => item.amount));
  check(data.lineItemsNetTotal, lines, ['lineItemsNetTotal'], 'LINE_SUM_MISMATCH');
  check(data.documentLevelAllowancesTotal, allowances, ['documentLevelAllowancesTotal'], 'ALLOWANCE_SUM_MISMATCH');
  check(data.documentLevelChargesTotal, charges, ['documentLevelChargesTotal'], 'CHARGE_SUM_MISMATCH');
  check(data.totalNetAmount, lines - allowances + charges, ['totalNetAmount'], 'NET_TOTAL_MISMATCH');
  adjustments.forEach((item, index) => {
    const soleGroup = groups.size === 1 ? groups.values().next().value : undefined;
    const category = item.taxCategory ?? soleGroup?.category;
    const rate = item.taxRate ?? soleGroup?.rate;
    if (category === undefined || rate === undefined) {
      issue('ADJUSTMENT_TAX_REQUIRED', ['allowancesCharges', index], 'Dokumentrabatt/-zuschlag benötigt eine eindeutige Steuerzuordnung.');
      return;
    }
    if (zeroTax.has(category) && rate !== 0) {
      issue('TAX_CATEGORY_RATE_MISMATCH', ['allowancesCharges', index, 'taxRate'], 'Steuerfreie Zuordnung benötigt einen Steuersatz von 0.');
    }
    addGroup(category, rate, money(item.amount) * (item.chargeIndicator ? 1n : -1n));
  });
  const seen = new Set<string>();
  let expectedTax = 0n;
  for (const group of groups.values()) {
    expectedTax += cents(divide(multiply({ n: group.base, d: 100n }, decimal(group.rate)), decimal(100)));
  }
  data.taxBreakdown.forEach((group, index) => {
    const id = key(group.taxCategory, group.taxRate);
    const expected = groups.get(id);
    if (!expected || seen.has(id)) {
      issue('TAX_GROUP_MISMATCH', ['taxBreakdown', index], 'Steuergruppe ist doppelt oder passt nicht zu den Positionen.');
    } else {
      check(group.taxableAmount, expected.base, ['taxBreakdown', index, 'taxableAmount'], 'TAX_BASE_MISMATCH');
      check(group.taxAmount, cents(divide(multiply({ n: expected.base, d: 100n }, decimal(expected.rate)), decimal(100))), ['taxBreakdown', index, 'taxAmount'], 'TAX_AMOUNT_MISMATCH');
    }
    seen.add(id);
  });
  for (const id of groups.keys()) {
    if (!seen.has(id)) issue('TAX_GROUP_MISSING', ['taxBreakdown'], 'Eine Steuergruppe der Positionen fehlt.');
  }
  check(data.totalTaxAmount, expectedTax, ['totalTaxAmount'], 'TAX_TOTAL_MISMATCH');
  check(data.totalTaxAmount, sumMoney(data.taxBreakdown.map(group => group.taxAmount)), ['totalTaxAmount'], 'TAX_SUM_MISMATCH');
  check(data.totalGrossAmount, money(data.totalNetAmount) + money(data.totalTaxAmount), ['totalGrossAmount'], 'GROSS_TOTAL_MISMATCH');
  check(data.amountDue, money(data.totalGrossAmount) - money(data.prepaidAmount ?? 0) + money(data.roundingAmount ?? 0), ['amountDue'], 'AMOUNT_DUE_MISMATCH');
  return issues;
}

/** Mahnungen carry existing gross debt plus final fees; debt is not taxed again. */
export function validateDunningConsistency(data: import('./schemas').DunningConsistencyData): InvoiceConsistencyIssue[] {
  const issues: InvoiceConsistencyIssue[] = [];
  const issue = (code: string, path: (string | number)[], message: string) => issues.push({ code, path, message });
  const check = (actual: number, expected: bigint, path: (string | number)[], code: string) => {
    if (Number.isFinite(actual) && absolute(money(actual) - expected) > 1n) {
      issue(code, path, `${code}: Betrag ist inkonsistent; erwartet ${amount(expected).toFixed(2)}.`);
    }
  };
  const references = new Set<string>();
  const ids = new Set<string>();
  data.referencedInvoices.forEach((invoice, index) => {
    const path = ['referencedInvoices', index];
    if (invoice.currency !== data.currency) issue('DUNNING_CURRENCY_MISMATCH', [...path, 'currency'], 'Offene Beträge müssen dieselbe Währung wie die Mahnung haben.');
    if (Number.isFinite(invoice.openAmount) && Number.isFinite(invoice.totalAmount) && money(invoice.openAmount) > money(invoice.totalAmount) + 1n) {
      issue('DUNNING_OPEN_AMOUNT_EXCEEDS_TOTAL', [...path, 'openAmount'], 'Offener Betrag übersteigt den ursprünglichen Rechnungsbetrag.');
    }
    if (references.has(invoice.invoiceNumber) || (invoice.invoiceId && ids.has(invoice.invoiceId))) {
      issue('DUNNING_DUPLICATE_REFERENCE', [...path, 'invoiceNumber'], 'Eine Rechnung darf in der Mahnung nicht doppelt gezählt werden.');
    }
    references.add(invoice.invoiceNumber);
    if (invoice.invoiceId) ids.add(invoice.invoiceId);
  });
  if (data.referencedInvoices.every(invoice => Number.isFinite(invoice.openAmount))) {
    check(data.totalOpenAmount, sumMoney(data.referencedInvoices.map(invoice => invoice.openAmount)), ['totalOpenAmount'], 'DUNNING_OPEN_SUM_MISMATCH');
  }
  const fees = data.dunningFees ?? [];
  fees.forEach((fee, index) => {
    if (fee.netAmount === undefined && fee.taxAmount === undefined && fee.taxRate === undefined) return;
    if (fee.netAmount === undefined || fee.taxAmount === undefined || fee.taxRate === undefined) {
      issue('DUNNING_FEE_TAX_INCOMPLETE', ['dunningFees', index], 'Eine ausgewiesene Gebührensteuer benötigt Netto, Steuerbetrag und Steuersatz.');
    } else if ([fee.netAmount, fee.taxAmount, fee.taxRate].every(Number.isFinite)) {
      check(fee.taxAmount, cents(divide(multiply(decimal(fee.netAmount), decimal(fee.taxRate)), decimal(100))), ['dunningFees', index, 'taxAmount'], 'DUNNING_FEE_TAX_MISMATCH');
      check(fee.amount, money(fee.netAmount) + money(fee.taxAmount), ['dunningFees', index, 'amount'], 'DUNNING_FEE_GROSS_MISMATCH');
    }
  });
  if (Number.isFinite(data.totalOpenAmount) && fees.every(fee => Number.isFinite(fee.amount))) {
    check(data.totalDunningAmount, money(data.totalOpenAmount) + sumMoney(fees.map(fee => fee.amount)), ['totalDunningAmount'], 'DUNNING_TOTAL_MISMATCH');
  }
  return issues;
}
